import {BlockUserFromOrgDialog} from '@github-ui/commenting/BlockUserFromOrgDialog'
import {unblockUserPersonally} from '@github-ui/commenting/mutations/unblock-user-personally-mutation'
import {PersonalBlockUserDialog} from '@github-ui/commenting/PersonalBlockUserDialog'
import {UnblockUserFromOrgDialog} from '@github-ui/commenting/UnblockUserFromOrgDialog'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {ConfirmationDialog} from '@primer/react'
import {useBlockUserFromOrgMutation} from '../../mutations/use-block-user-from-org-mutation'
import {useUnblockUserFromOrgMutation} from '../../mutations/use-unblock-user-from-org-mutation'

interface PersonalBlockDialogProps {
  authorLogin: string
  contentId: string
  contentUrl: string
  isOpen: boolean
  onClose: () => void
}

export function PersonalBlockDialog({authorLogin, contentId, contentUrl, isOpen, onClose}: PersonalBlockDialogProps) {
  if (!isOpen) return null

  return (
    <PersonalBlockUserDialog
      contentId={contentId}
      contentAuthor={{login: authorLogin}}
      contentUrl={contentUrl}
      onClose={onClose}
    />
  )
}

interface PersonalUnblockDialogProps {
  authorLogin: string
  isOpen: boolean
  onClose: () => void
}

export function PersonalUnblockDialog({authorLogin, isOpen, onClose}: PersonalUnblockDialogProps) {
  const {addToast} = useToastContext()

  if (!isOpen) return null

  return (
    <ConfirmationDialog
      title={`Unblock @${authorLogin}`}
      confirmButtonContent="Unblock user"
      confirmButtonType="danger"
      onClose={gesture => {
        if (gesture === 'confirm') {
          void (async () => {
            try {
              await unblockUserPersonally(authorLogin)
              window.location.reload()
            } catch {
              // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
              addToast({type: 'error', message: 'Failed to unblock user'})
            }
          })()
        }
        onClose()
      }}
    >
      Are you sure you want to unblock @{authorLogin}?
    </ConfirmationDialog>
  )
}

interface OrganizationBlockDialogProps {
  authorLogin: string
  contentId: string
  contentUrl: string
  isOpen: boolean
  organizationLogin: string
  onClose: () => void
}

export function OrganizationBlockDialog({
  authorLogin,
  contentId,
  contentUrl,
  isOpen,
  organizationLogin,
  onClose,
}: OrganizationBlockDialogProps) {
  const {addToast} = useToastContext()
  const {mutate: blockUserFromOrg} = useBlockUserFromOrgMutation()

  if (!isOpen) return null

  return (
    <BlockUserFromOrgDialog
      organization={{login: organizationLogin}}
      contentId={contentId}
      contentAuthor={{login: authorLogin}}
      contentUrl={contentUrl}
      onBlock={(
        duration,
        shouldHideComment,
        hiddenReason,
        organization,
        notifyBlockedUser,
        userLogin,
        note,
        closeContent,
      ) => {
        blockUserFromOrg(
          {
            closeContent,
            duration,
            hiddenReason,
            note,
            notifyBlockedUser,
            organizationLogin: organization,
            shouldHideComment,
            userLogin,
          },
          {
            onError: () => {
              // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
              addToast({type: 'error', message: 'Failed to block user'})
            },
            onSuccess: () => window.location.reload(),
          },
        )
      }}
      onClose={onClose}
    />
  )
}

interface OrganizationUnblockDialogProps {
  authorLogin: string
  contentId: string
  isOpen: boolean
  organizationLogin: string
  onClose: () => void
}

export function OrganizationUnblockDialog({
  authorLogin,
  contentId,
  isOpen,
  organizationLogin,
  onClose,
}: OrganizationUnblockDialogProps) {
  const {addToast} = useToastContext()
  const {mutate: unblockUserFromOrg} = useUnblockUserFromOrgMutation()

  if (!isOpen) return null

  return (
    <UnblockUserFromOrgDialog
      organization={{login: organizationLogin}}
      contentAuthor={{login: authorLogin}}
      contentId={contentId}
      onUnblock={(organization, userLogin) => {
        unblockUserFromOrg(
          {organizationLogin: organization, userLogin},
          {
            onError: () => {
              // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
              addToast({type: 'error', message: 'Failed to unblock user'})
            },
            onSuccess: () => window.location.reload(),
          },
        )
      }}
      onClose={onClose}
    />
  )
}
